import { useState, useEffect } from 'react';
import * as THREE from 'three';
import { useSketchStore } from '../../state/useSketchStore';
import { sweepFace } from '../../core/sketch/sweep';
import { useSketchToolEnv } from './sketchInput';
import { registerSketchTool, type SketchHit } from './sketchToolBus';

/**
 * Sketch utility tools (Tape Measure, Paint Bucket, Follow Me, Section Plane,
 * Insert Component).
 */

const GUIDE_EXTENT = 1000; // infinite-feel length for guide lines

/** Long-lived guide id factory. */
const newGuideId = (): string => `guide-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// ============================== TAPE MEASURE ==============================

export function useSketchTapeMeasureTool() {
    const env = useSketchToolEnv();
    const [first, setFirst] = useState<THREE.Vector3 | null>(null);
    /** Offset-guide mode: the model edge the guide runs parallel to. */
    const [edgeRef, setEdgeRef] = useState<{ a: THREE.Vector3; b: THREE.Vector3 } | null>(null);
    const [cursor, setCursor] = useState<THREE.Vector3 | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                const res = env.infer(hit, [], null);
                setCursor(res.point.clone());
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, [], null);
                const point = res.point.clone();

                // OFFSET-GUIDE MODE: first click on a model edge arms a
                // parallel guide (Sketch's tape-on-edge behavior); the
                // second click drops the infinite guide through the cursor.
                if (!first && res.kind === 'onEdge' && res.edge) {
                    setEdgeRef(res.edge);
                    setFirst(point);
                    return;
                }
                if (first && edgeRef) {
                    const dir = edgeRef.b.clone().sub(edgeRef.a).normalize();
                    useSketchStore.getState().addGuide({
                        id: newGuideId(),
                        kind: 'line',
                        a: [point.x - dir.x * GUIDE_EXTENT, point.y - dir.y * GUIDE_EXTENT, point.z - dir.z * GUIDE_EXTENT],
                        b: [point.x + dir.x * GUIDE_EXTENT, point.y + dir.y * GUIDE_EXTENT, point.z + dir.z * GUIDE_EXTENT],
                    }, 'Add Parallel Guide');
                    setFirst(null);
                    setEdgeRef(null);
                    return;
                }

                // MEASURE MODE: two clicks report the distance; Ctrl/Cmd
                // keeps a finite guide line, Alt drops a guide point.
                if (!first) {
                    setFirst(point);
                    return;
                }
                const distance = first.distanceTo(point);
                useSketchStore.getState().setVcbText(distance.toFixed(3));
                if (hit.ctrlKey) {
                    useSketchStore.getState().addGuide({
                        id: newGuideId(),
                        kind: 'line',
                        a: [first.x, first.y, first.z],
                        b: [point.x, point.y, point.z],
                    }, 'Add Guide');
                } else if (hit.shiftKey) {
                    useSketchStore.getState().addGuide({
                        id: newGuideId(),
                        kind: 'point',
                        a: [point.x, point.y, point.z],
                    }, 'Add Guide Point');
                }
                setFirst(null);
            },
        });
        return () => registerSketchTool(null);
    }, [first, edgeRef, env]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setFirst(null);
            setEdgeRef(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { first, edgeRef, cursor };
}

// ============================== PAINT BUCKET ==============================

export function useSketchPaintBucketTool() {
    useEffect(() => {
        registerSketchTool({
            onDown: (hit) => {
                if (hit.button !== 0 || hit.kind !== 'face' || !hit.solidId || !hit.faceId) return;
                const store = useSketchStore.getState();
                const solid = store.solids[hit.solidId];
                const face = solid?.faces.find((f) => f.id === hit.faceId);
                if (!solid || !face) return;
                // Alt+click samples the face's material (the pipet modifier).
                if (hit.altKey) {
                    const sampled = face.materialId ?? solid.materialId;
                    store.setSelectedMaterial(sampled);
                    return;
                }
                store.setSolidMaterial([hit.solidId], [hit.faceId], store.selectedMaterialId, 'Paint');
            },
        });
        return () => registerSketchTool(null);
    }, []);
    return {};
}

// ============================== FOLLOW ME ==============================

export function useSketchFollowMeTool() {
    const [profile, setProfile] = useState<string | null>(null); // solidId:faceId
    const [path, setPath] = useState<THREE.Vector3[]>([]);
    const [cursor, setCursor] = useState<THREE.Vector3 | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => setCursor(hit.point.clone()),
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const point = hit.point.clone();
                if (!profile) {
                    if (hit.kind !== 'face' || !hit.solidId || !hit.faceId) return;
                    setProfile(`${hit.solidId}:${hit.faceId}`);
                    setPath([point]);
                    return;
                }
                // Path points on the plane perpendicular to the profile's
                // normal is the Follow Me contract; v1 lets the user click
                // freely and Enter/double-click commits.
                setPath((p) => [...p, point]);
            },
        });
        return () => registerSketchTool(null);
    }, [profile]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                setProfile(null);
                setPath([]);
                return;
            }
            if (e.key !== 'Enter' || !profile || path.length < 2) return;
            const [solidId, faceId] = profile.split(':');
            const store = useSketchStore.getState();
            const solid = store.solids[solidId];
            if (solid) {
                const result = sweepFace(solid, faceId, path);
                if (result.ok && result.solid) {
                    store.updateSolid(solid.id, result.solid, 'Follow Me');
                }
            }
            setProfile(null);
            setPath([]);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [profile, path]);

    return { profile, path, cursor };
}

// ============================== SECTION PLANE ==============================

/**
 * Place the plane, select it as an entity and hand the pointer to Select so
 * the gumball offers move/rotate on the fresh section right away (the same
 * flow Insert Component uses to make a placed instance immediately usable).
 */
function activateSectionPlane(plane: { normal: [number, number, number]; constant: number }): void {
    const store = useSketchStore.getState();
    store.setSectionPlane(plane);
    store.select([]);
    store.setSectionPlaneSelected(true);
    import('../../state/store').then(({ useAppStore }) => {
        useAppStore.getState().select([]);
        useAppStore.getState().setActiveTool('select');
    });
}

export function useSketchSectionPlaneTool() {
    useEffect(() => {
        registerSketchTool({
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const store = useSketchStore.getState();
                // A clipping box outranks the plane: any Section Plane click
                // clears it first (the menu's Remove Clipping Box does too).
                if (store.sectionBox) {
                    store.setSectionBox(null);
                    return;
                }
                if (store.sectionPlane) {
                    // Toggle off when a section is already active.
                    store.setSectionPlane(null);
                    return;
                }
                if (hit.kind === 'face' && hit.solidId && hit.faceId) {
                    const solid = store.solids[hit.solidId];
                    const face = solid?.faces.find((f) => f.id === hit.faceId);
                    if (face) {
                        const n = new THREE.Vector3(...face.normal).normalize();
                        const d = -n.dot(hit.point);
                        activateSectionPlane({ normal: [n.x, n.y, n.z], constant: d });
                        return;
                    }
                }
                // Empty-space fallback: a horizontal section at the model's
                // vertical midpoint. A ground-level plane (Z=0, where the
                // event plane reports hits) would clip nothing and read as a
                // dead tool.
                const box = new THREE.Box3();
                Object.values(store.solids).forEach((solid) => {
                    if (!solid.visible) return;
                    solid.faces.forEach((face) => {
                        face.outer.forEach((p) => box.expandByPoint(new THREE.Vector3(...p)));
                    });
                });
                const midZ = box.isEmpty() ? 0 : box.getCenter(new THREE.Vector3()).z;
                activateSectionPlane({ normal: [0, 0, 1], constant: -midZ });
            },
        });
        return () => registerSketchTool(null);
    }, []);
    return {};
}

// ============================== INSERT COMPONENT ==============================

let pendingInsertDefId: string | null = null;

/** Arm the Insert tool with a component definition id (library click). */
export function armInsertComponent(defId: string): void {
    pendingInsertDefId = defId;
}

/** Insert tool: click places the armed definition (ghost follows cursor). */
export function useSketchInsertComponentTool() {
    const [ghost, setGhost] = useState<THREE.Vector3 | null>(null);

    useEffect(() => {
        if (!pendingInsertDefId) return;
        registerSketchTool({
            onMove: (hit) => setGhost(hit.point.clone()),
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const defId = pendingInsertDefId;
                pendingInsertDefId = null;
                registerSketchTool(null);
                setGhost(null);
                if (!defId) return;
                const store = useSketchStore.getState();
                const p = hit.point;
                store.insertComponent(defId, [p.x, p.y, p.z]);
                // Back to Select so the placed instance is immediately usable.
                import('../../state/store').then(({ useAppStore }) => {
                    useAppStore.getState().setActiveTool('select');
                });
            },
        });
        return () => {
            registerSketchTool(null);
        };
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            pendingInsertDefId = null;
            setGhost(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { ghost };
}
